import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconCalendarWeek } from '@calendly/ui/components/glyphs/IconCalendarWeek';
import { IconCircleX } from '@calendly/ui/components/glyphs/IconCircleX';
import { IconCopy } from '@calendly/ui/components/glyphs/IconCopy';
import { IconDotsVertical } from '@calendly/ui/components/glyphs/IconDotsVertical';
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink';
import { IconPencil } from '@calendly/ui/components/glyphs/IconPencil';
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash';
import { IconButton } from '@calendly/ui/components/icon-button';
import { BareItem, Group, Item } from '@calendly/ui/components/menu';
import { SimplePopup } from '@calendly/ui/components/popup';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { duplicatePoll, editPoll } from 'features/one_off_modal';
import { CopyLinkButton, useOpenEvent, ClearReservedTimesModal } from '../shared';
import { useSendMeetingPollShared, useTrackMeetingInteraction } from './analytics';
import { useDeletePollMutation } from './api';
import { ViewResultsButton } from './components/view_results_button';
import { getUniqInvitees, useClosePollSidebar } from './helpers';
const containerCss = "cy5sroa";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const popupDescriptionCss = "pv5j1y9";
const PopupDescription = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(popupDescriptionCss, props.className)} />;
});
const ViewBookingPage = ({
  poll
}) => <Item as="a" href={poll.bookingUrl} target="_blank" rel="noopener noreferrer">
    <UiIcon icon={IconExternalLink} size="small" />
    {I18n.t('publisher.scheduling.polls.action.view_live_page')}
  </Item>;
const CopyLink = ({
  poll
}) => {
  const sendMeetingPollShared = useSendMeetingPollShared(poll);
  const trackMeetingInteraction = useTrackMeetingInteraction(poll, 'copy_link');
  const onClick = () => {
    sendMeetingPollShared();
    trackMeetingInteraction();
  };
  return <CopyLinkButton url={poll.bookingUrl} as={BareItem} onClick={onClick} />;
};
const ViewResults = ({
  poll
}) => <ViewResultsButton poll={poll} as={Item} />;
const Edit = ({
  poll
}) => {
  const dispatch = useDispatch();
  const trackMeetingInteraction = useTrackMeetingInteraction(poll, 'edit');
  const handleClick = () => {
    dispatch(editPoll({
      eventTypeId: poll.id
    }));
    trackMeetingInteraction();
  };
  return <Item onClick={handleClick}>
      <UiIcon icon={IconPencil} size="small" />
      {I18n.t('publisher.action.edit')}
    </Item>;
};
const Duplicate = ({
  poll
}) => {
  const dispatch = useDispatch();
  const trackMeetingInteraction = useTrackMeetingInteraction(poll, 'duplicate');
  const handleClick = () => {
    dispatch(duplicatePoll({
      eventTypeId: poll.id
    }));
    trackMeetingInteraction();
  };
  return <Item onClick={handleClick}>
      <UiIcon icon={IconCopy} size="small" />
      {I18n.t('publisher.action.duplicate')}
    </Item>;
};
const Delete = ({
  onClick
}) => <Item onClick={onClick}>
    <UiIcon icon={IconTrash} size="small" />
    {I18n.t('publisher.action.delete')}
  </Item>;
const ViewMeetingIconButton = ({
  poll
}) => {
  const trackMeetingInteraction = useTrackMeetingInteraction(poll, 'view_meeting');
  const handleViewMeeting = useOpenEvent(poll.event);
  const handleClick = () => {
    trackMeetingInteraction();
    handleViewMeeting();
  };
  return <Item onClick={handleClick}>
      <UiIcon icon={IconCalendarWeek} size="small" />
      {I18n.t('publisher.scheduling.shared.action.view_meeting')}
    </Item>;
};
const ClearReservedTimesButton = ({
  poll,
  setPendingEventTypeId
}) => <Item onClick={() => setPendingEventTypeId(poll.id)}>
    <UiIcon icon={IconCircleX} size="small" />
    {I18n.t('publisher.scheduled_events.pending.reserved_times.clear_title')}
  </Item>;
export const Menu = ({
  poll,
  fullActionsList = false
}) => {
  const [toggle, Dropdown] = useDropdown();
  const [deletePoll] = useDeletePollMutation();
  const [isDeletePopupOpen, openDeletePopup, closeDeletePopup] = useBooleanState(false);
  const closePollSidebar = useClosePollSidebar();
  const bookedMeeting = poll.status === 'booked' && !!poll.event;
  const trackMeetingInteraction = useTrackMeetingInteraction(poll, 'delete');
  const [pendingEventTypeId, setPendingEventTypeId] = useState(null);
  const handleDelete = () => {
    trackMeetingInteraction();
    deletePoll(poll.id);
    closePollSidebar();
  };
  const menuItems = bookedMeeting ? [{
    key: 'view-meeting',
    show: true,
    Component: ViewMeetingIconButton
  }] : [{
    key: 'copy-link',
    show: poll.status === 'created' && (fullActionsList || getUniqInvitees(poll).length > 0),
    Component: CopyLink
  }, {
    key: 'view-live-page',
    show: poll.status === 'created',
    Component: ViewBookingPage
  }, {
    key: 'clear-reserved-times',
    show: poll.status === 'created' && poll.canClearReservedTimes,
    Component: ClearReservedTimesButton
  }, {
    key: 'view-results',
    show: fullActionsList,
    Component: ViewResults
  }, {
    key: 'duplicate',
    show: poll.status !== 'booked',
    Component: Duplicate
  }, {
    key: 'edit',
    show: poll.status === 'created',
    Component: Edit
  }].filter(({
    show
  }) => show);
  return <Container>
      <IconButton size="small" aria-label={I18n.t('publisher.components.table.action_menu_for', {
      item: poll.name
    })} onClick={toggle}>
        <UiIcon icon={IconDotsVertical} size="small" />
      </IconButton>
      <Dropdown>
        <ViewportDropdownMenu>
          {menuItems.length > 0 && <Group>
              {menuItems.map(({
            key,
            Component
          }) => <Component key={key} poll={poll} setPendingEventTypeId={setPendingEventTypeId} />)}
            </Group>}
          {poll.status !== 'booked' && <Group>
              <Delete onClick={openDeletePopup} />
            </Group>}
        </ViewportDropdownMenu>
      </Dropdown>
      <SimplePopup isOpen={isDeletePopupOpen} onClose={closeDeletePopup} title={I18n.t('publisher.scheduling.shared.popups.delete_title')} cancelLabel={I18n.t('publisher.action.cancel')} confirmLabel={I18n.t('publisher.action.delete')} onConfirm={handleDelete}>
        <PopupDescription>
          {I18n.t('publisher.scheduling.shared.popups.delete_description')}
        </PopupDescription>
      </SimplePopup>
      <ClearReservedTimesModal triggerEventUUID={pendingEventTypeId} isPoll onClose={() => setPendingEventTypeId(null)} />
    </Container>;
};

require("./menu.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./menu.js");